import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { differenceInSeconds } from 'date-fns';
import get from 'lodash.get';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelector, actions as cartActions, Cart } from 'services/cart';
import { selectors as configSelectors, Configuration, Messages } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { formatText, formatTitle } from 'utils/stringFormat';

const getExpirationAndCurrentTime = (expiration: string | Date) => {
  const currentTime = new Date().getTime();
  const expirationTime = new Date(expiration).getTime();
  return { currentTime, expirationTime };
};

const getDifferenceInSeconds = (expiration: string | Date) => {
  const { currentTime, expirationTime } = getExpirationAndCurrentTime(expiration);
  return differenceInSeconds(expirationTime, currentTime);
};

/**
 * Tracks the cart expiration and displays an overlay when the time is reached.
 * Showing the amount of time left was determined to not be useful, and was removed.
 */
export const CartExpiration = () => {
  const { t, i18n } = useTranslation();

  const { releaseCart } = useActions(cartActions);

  const navigate = useNavigate();
  const cart = useSelector(cartSelector.selectCart) as Cart;
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const [timeDifference, setTimeDifference] = React.useState(getDifferenceInSeconds(cart.expiration));
  const interval = React.useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const cartExpired = timeDifference <= 0;

  const flowTemplate = get(configuration, 'configuration.flowTemplate');
  const palette = get(configuration, 'configuration.globalSettings.palette');
  const cartLabel = get(configuration, `configuration.labels.cart.${i18n.language}`, t('cart.cartLabel'));
  const messages: Messages = get(configuration, `${ticketingPath}.cart.expirationOverlayMessages`, {});
  const header = get(
    messages,
    `header.${i18n.language}`,
    t('cartExpiration.header', { cartLabel: formatTitle(cartLabel, i18n.language) })
  );
  const subheader = get(messages, `subheader.${i18n.language}`, t('cartExpiration.subheader'));
  const confirmButton = get(messages, `buttons.${i18n.language}[0]`, t('commonText.startOver'));

  React.useEffect(() => {
    if (cartExpired) {
      clearInterval(interval.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeDifference]);

  React.useEffect(() => {
    interval.current = setInterval(() => {
      setTimeDifference(getDifferenceInSeconds(cart.expiration));
    }, 1000);
    return () => clearInterval(interval.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onActionConfirm = () => {
    releaseCart(cart.releaseParams);
    navigate(`/ticketing/attract`);
  };

  const backgroundStyle = () => {
    if (palette && palette.darkPrimary) {
      return { background: `${palette.darkPrimary}e5` };
    }
    return { background: 'rgba(255,255,255,0.95)' };
  };

  return (
    <>
      {cartExpired && (
        <ActionOverlay
          flow={flowTemplate}
          title={formatText(header, flowTemplate)}
          description={subheader}
          confirmText={formatText(confirmButton, flowTemplate)}
          onConfirm={onActionConfirm}
          height={500}
          style={backgroundStyle()}
        />
      )}
    </>
  );
};

export default CartExpiration;
